import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import WithConfirm from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import EmptyState from '~/components/core/EmptyState';
import RoundEmptyStateWrapper from '~/components/core/EmptyState/RoundEmptyStateWrapper';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useCms } from '~/components/hooks/useCms';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { CheckIcon, DownloadIcon, PencilIcon, PolicyIcon, RemoveIcon, TrashIcon } from '~/components/icons';
import Switch from '~/components/ui/atoms/Switch';
import DAMAGE_ASSESSMENT_TYPES from '~/server_shared/generated-types/DAMAGE_ASSESSMENT_TYPES';
import { reportAxiosError, subOrgIdToNameDict } from '~/Utils';
import { isLocaleRegionIsUs } from '~/Utils/regionUtils';

import { COVERAGE_CONFIGURATION_LIMITS, INVOLVED_DICT_PER_CLAIM_TYPE, LOSS_TYPES } from '../../../Types';
import { getLobDescription, getLobIcon } from '../../../Utils/lobUtils';
import CardDialog from '../../CardDialog';
import { Heading, Label, LoadingSwitch, MultiSelectFilter, SortableTable, Text } from '../../core';
import InlineIconButton from '../../InlineIconButton';
import { usePrevious } from '../../TextFieldFormik';

import { CoverageDialog } from './CoverageDialog';
import { SharedCoverageDialog } from './SharedCoverageDialog';

import { useStyles } from '../../../assets/styles';
import styles from './coverageConfiguration.module.scss';

const COVERAGES_SUBTITLE = 'Manage your organization coverages.';

const COVERAGE_CONFIGURATION_BOOLEAN_FIELDS = {
  ...COVERAGE_CONFIGURATION_LIMITS,
  is_capacity_agreement_enabled: {
    desc: 'Capacity Agreement Enabled',
  },
  pre_fill_deductible: {
    desc: 'Pre-fill Deductible',
  },
};

const ExposureConfigurationTool = ({
  isLoading,
  isError,
  organization,
  supportedClaimTypes,
  coverages,
  sharedCoverages,
  coverageConfiguration,
  onUpdateCoverage,
  onCreateCoverage,
  onUpdateSharedCoverage,
  onCreateSharedCoverage,
  onToggleGeneralExpenses,
  onUpdateCoverageConfiguration,
  onDeleteCoverage,
  onDeleteSharedCoverage,
  enableRemovingExistingCoverages,
  fetchUsedCoveragesKeys,
  fetchUsedSharedCoveragesKeys,
  fetchCoverages,
  errorHandler,
  hideExposureConfigurationFilters,
  wizardLob,
  overrideDisabled,
  onDisable,
  onExportToExcel,
  isExporting = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedLOB, setSelectedLOB] = useState([]);
  const [selectedSubOrgs, setSelectedSubOrgs] = useState([]);
  const [showCoverageDialog, setShowCoverageDialog] = useState(false);
  const [showSharedCoverageDialog, setShowSharedCoverageDialog] = useState(false);
  const [coverageToEdit, setCoverageToEdit] = useState(null);
  const [sharedCoverageToEdit, setSharedCoverageToEdit] = useState(null);
  const [isGeneralExpensesLoading, setIsGeneralExpensesLoading] = useState(false);
  const subOrgIdToName = subOrgIdToNameDict(organization);
  const shouldShowISOCode = isLocaleRegionIsUs();
  const prevSelectedSubOrgsRef = usePrevious(selectedSubOrgs);
  const { lobConfigurationsDict = {} } = useLobConfiguration();
  // The non-impersonated organization: `organization` is the org being viewed, so gating on it
  // would show the button to a customer org looking at its own configuration.
  const { userOrganization } = useCms();
  const canExportToExcel = Boolean(onExportToExcel) && Boolean(userOrganization?.is_super_organization);

  useEffect(() => {
    if (selectedSubOrgs.includes('all') && !prevSelectedSubOrgsRef?.includes('all')) {
      setSelectedSubOrgs(['all']);
    } else if (
      prevSelectedSubOrgsRef?.includes('all') &&
      selectedSubOrgs.includes('all') &&
      selectedSubOrgs.length > 1
    ) {
      setSelectedSubOrgs(selectedSubOrgs.filter((e) => e !== 'all'));
    }
  }, [selectedSubOrgs, setSelectedSubOrgs, prevSelectedSubOrgsRef]);

  const { classes } = useStyles();

  // 'all' is the filter's own sentinel for "no narrowing", not a sub-organization id: it is the
  // option labelled *All sub-organizations*, and the effect above makes it exclusive of every real
  // id. Dropping it here is what makes that label true -- matched against `sub_organizations` it
  // hits nothing, so the table used to fall back to org-level rows only.
  const selectedSubOrgIds = useMemo(() => selectedSubOrgs.filter((subOrg) => subOrg !== 'all'), [selectedSubOrgs]);

  // MIRRORED: the Excel export re-implements this predicate server-side, in
  // coverage_config_export_manager.py (`_coverage_passes`, and `_shared_coverage_passes` for the
  // predicate below), so that an export reproduces the filtered view rather than the whole
  // configuration. A change here needs the same change there -- nothing fails if you forget.
  const isCoverageFitFilters = useCallback(
    (coverage) => {
      const doesMatchText =
        (coverage.display_name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (coverage.coverage_key?.toLowerCase() || '').includes(searchText?.toLowerCase());
      const doesMatchLOB = selectedLOB.length === 0 || selectedLOB.includes(coverage.lob);
      const doesMatchSubOrgs =
        selectedSubOrgIds.length === 0 ||
        coverage.is_org_level ||
        selectedSubOrgIds.every((subOrg) =>
          coverage.sub_organizations.some((coverageSubOrg) => coverageSubOrg.id === subOrg)
        );

      return doesMatchText && doesMatchLOB && doesMatchSubOrgs;
    },
    [searchText, selectedLOB, selectedSubOrgIds]
  );

  const isSharedCoverageFitFilters = useCallback(
    (sharedCoverage) => {
      const doesMatchText =
        sharedCoverage.coverage_config_display_names
          .concat(sharedCoverage.coverage_config_keys)
          .some((x) => (x?.toLowerCase() || '').includes(searchText.toLowerCase())) ||
        `${sharedCoverage.shared_coverage_key?.toLowerCase() || ''}`.includes(searchText.toLowerCase());
      const doesMatchLOB = selectedLOB.length === 0 || selectedLOB.includes(sharedCoverage.lob);
      const doesMatchSubOrgs =
        selectedSubOrgIds.length === 0 ||
        sharedCoverage.is_org_level ||
        selectedSubOrgIds.every((subOrg) =>
          sharedCoverage.sub_organizations.some((coverageSubOrg) => coverageSubOrg.id === subOrg)
        );

      return doesMatchText && doesMatchLOB && doesMatchSubOrgs;
    },
    [searchText, selectedLOB, selectedSubOrgIds]
  );

  const onClose = () => {
    setCoverageToEdit(null);
    setShowCoverageDialog(false);
  };

  const convertServerCoverageConfigToFormikValues = (coverageConfig) => {
    return {
      ...coverageConfig,
      sub_organization_ids: coverageConfig.is_org_level
        ? ['all']
        : coverageConfig.sub_organizations.map((sub_org) => sub_org.id),
    };
  };

  const yesOption = <CheckIcon width={18} height={18} iconColor="#909090" />;

  const actionsCell = ({ onDelete, onEdit, onDisable }) => ({
    id: 'actions',
    numeric: false,
    label: 'Actions',
    specialCell: (config) => (
      <div>
        <InlineIconButton
          icon={PencilIcon}
          tooltipTitle="Edit"
          className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
          onClick={() => onEdit(config)}
          disabled={config?.is_disabled}
        />
        {onDelete && enableRemovingExistingCoverages && (
          <InlineIconButton
            icon={TrashIcon}
            tooltipTitle="Delete"
            className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
            onClick={() => onDelete(config)}
            disabled={config?.is_disabled}
          />
        )}
        {onDisable && (
          <WithConfirm
            title="Are you sure?"
            contentText="You will not be able to add exposures on this coverage anymore. Previous exposures with this coverage will still work. This action cannot be undone."
            primaryButtonName="Disable"
            shouldCloseOnPrimary
          >
            <InlineIconButton
              icon={TrashIcon}
              tooltipTitle="Disable"
              className={`${classes.textIcon} ${classes.marginedIcon} ${classes.hoverableNonFilledIcon}`}
              onClick={async () => await onDisable(config)}
              disabled={config?.is_disabled}
            />
          </WithConfirm>
        )}
      </div>
    ),
  });

  const getInvolvedTypeDescription = (coverageConfig, involved_type, lobConfigurationsDict) => {
    return (
      INVOLVED_DICT_PER_CLAIM_TYPE[coverageConfig?.lob]?.[involved_type]?.desc ||
      lobConfigurationsDict[coverageConfig?.lob]?.involved_dict?.[involved_type]?.desc
    );
  };

  // MIRRORED: the Excel export reproduces these columns and their labels in
  // server/modules/coverages_config/internal/managers/coverage_config_export_manager.py
  // (`_coverages_sheet`). Adding, removing or renaming a column here leaves the workbook behind
  // until that function is changed to match -- nothing fails if you forget.
  const columnsData = [
    {
      id: 'display_name',
      numeric: false,
      label: 'Display Name',
      specialCell: (coverageConfig) => coverageConfig.display_name,
    },
    {
      id: 'coverage_key',
      numeric: false,
      label: 'API Key',
      specialCell: (coverageConfig) => coverageConfig.coverage_key,
    },
    {
      id: 'iso_code',
      numeric: false,
      label: 'ISO Code',
      specialCell: (coverageConfig) => <TextCell value={coverageConfig.iso_code} />,
    },
    {
      id: 'damage_assessment_type',
      numeric: false,
      label: 'Damage Assessment Type',
      specialCell: (coverageConfig) => (
        <TextCell value={DAMAGE_ASSESSMENT_TYPES[coverageConfig?.damage_assessment_type]} />
      ),
    },
    {
      id: 'sub_organizations',
      numeric: false,
      label: 'Sub-Organizations',
      specialCell: (coverageConfig) => {
        return coverageConfig.is_org_level ? (
          <Chip key="all" size="small" label="All" disabled={coverageConfig?.is_disabled} />
        ) : (
          coverageConfig.sub_organizations?.map((subOrg) => (
            <Chip
              key={subOrg.id}
              size="small"
              label={subOrg.name}
              className={classes.chip}
              disabled={coverageConfig?.is_disabled}
            />
          ))
        );
      },
    },
    {
      id: 'lob',
      numeric: false,
      label: 'LOB',
      specialCell: (coverageConfig) => (
        <TextCell value={getLobDescription(coverageConfig.lob, lobConfigurationsDict)} />
      ),
    },
    {
      id: 'loss_type',
      numeric: false,
      label: 'Loss Type',
      specialCell: (coverageConfig) => <TextCell value={LOSS_TYPES[coverageConfig.loss_type]?.desc} />,
    },
    {
      id: 'involved_types',
      numeric: false,
      label: 'Involved Types',
      specialCell: (coverageConfig) =>
        Array.isArray(coverageConfig.involved_types)
          ? coverageConfig.involved_types.map((involved_type) => (
              <Chip
                key={involved_type}
                size="small"
                label={getInvolvedTypeDescription(coverageConfig, involved_type, lobConfigurationsDict)}
                className={classes.chip}
                disabled={coverageConfig?.is_disabled}
              />
            ))
          : null,
    },
    {
      id: 'coverage_group_display_name',
      label: 'Coverage Group',
    },
    {
      id: 'is_limit_per_person',
      numeric: false,
      label: 'Limit Per Person',
      specialCell: (coverageConfig) => (coverageConfig.is_limit_per_person ? yesOption : ''),
    },
    {
      id: 'is_limit_per_incident',
      numeric: false,
      label: 'Limit Per Incident',
      specialCell: (coverageConfig) => (coverageConfig.is_limit_per_incident ? yesOption : ''),
    },
    {
      id: 'is_deductible',
      numeric: false,
      label: 'Has Deductible',
      specialCell: (coverageConfig) => (coverageConfig.is_deductible ? yesOption : ''),
    },
    {
      id: 'is_coinsurance',
      numeric: false,
      label: 'Has Co-Insurance',
      specialCell: (coverageConfig) => (coverageConfig.is_coinsurance ? yesOption : ''),
    },
    {
      id: 'is_limit_per_policy_per_entity',
      numeric: false,
      label: 'Limit Per Policy Per Entity',
      specialCell: (coverageConfig) => (coverageConfig.is_limit_per_policy_per_entity ? yesOption : ''),
    },
    actionsCell({
      onDelete: onDeleteCoverage,
      onDisable,
      onEdit: (config) => {
        setCoverageToEdit(convertServerCoverageConfigToFormikValues(config));
        setShowCoverageDialog(true);
      },
    }),
  ]
    .filter((column) => column.id !== 'iso_code' || shouldShowISOCode)
    .filter((column) => column.id !== 'sub_organizations' || organization.sub_organizations_enabled);

  const sharedCoveragesColumnsData = [
    { id: 'shared_coverage_key', numeric: false, label: 'API Key' },
    {
      id: 'coverage_config_display_names',
      numeric: false,
      label: 'Coverages',
      specialCell: (sharedCoverageConfig) => sharedCoverageConfig.coverage_config_display_names.join(', '),
    },
    {
      id: 'sub_organizations',
      numeric: false,
      label: 'Sub-Organizations',
      specialCell: (sharedCoverageConfig) =>
        sharedCoverageConfig.is_org_level ? (
          <Chip key="all" size="small" label="All" />
        ) : (
          sharedCoverageConfig.sub_organizations.map((subOrg) => (
            <Chip key={subOrg.id} size="small" label={subOrg.name} className={classes.chip} />
          ))
        ),
    },
    {
      id: 'lob',
      numeric: false,
      label: 'LOB',
      specialCell: (sharedCoverageConfig) => (
        <TextCell value={getLobDescription(sharedCoverageConfig.lob, lobConfigurationsDict)} />
      ),
    },
    {
      id: 'is_limit_per_incident',
      numeric: false,
      label: 'Limit Per Incident',
      specialCell: (sharedCoverageConfig) => (sharedCoverageConfig.is_limit_per_incident ? yesOption : ''),
    },
    actionsCell({
      onDelete: onDeleteSharedCoverage,
      onEdit: (config) => {
        setSharedCoverageToEdit(convertServerCoverageConfigToFormikValues(config));
        setShowSharedCoverageDialog(true);
      },
    }),
  ].filter((column) => column.id !== 'sub_organizations' || organization.sub_organizations_enabled);

  const coveragesFiltered = useMemo(() => coverages?.filter(isCoverageFitFilters), [coverages, isCoverageFitFilters]);

  const shouldShowLobFilter = (supportedClaimTypes?.length ?? 0) > 1;

  const strikeOutRowIds = useMemo(
    () => coveragesFiltered?.filter((coverage) => coverage?.is_disabled)?.map((coverage) => coverage.id),
    [coveragesFiltered]
  );

  const addCoverageButton = (text, onClick) => (
    <Button color="primary" onClick={onClick} className="mb-12  mr-8">
      <AddIcon className={classes.leftButtonIcon} />
      {text}
    </Button>
  );

  const handleExportToExcel = () =>
    // Every filter isCoverageFitFilters ANDs has to go, or a filtered screen exports rows the
    // user filtered out and nothing says so. Passed as an object rather than positionally so a
    // fourth filter cannot be added in the wrong slot.
    onExportToExcel({
      subOrganizationIds: selectedSubOrgIds,
      searchText,
      lobs: selectedLOB,
    });

  const exportToExcelButton = canExportToExcel ? (
    <Button
      color="primary"
      onClick={handleExportToExcel}
      disabled={isExporting}
      className="mb-12 mr-8"
      startIcon={<DownloadIcon iconColor="currentColor" size={12} />}
    >
      Export to Excel
    </Button>
  ) : null;

  const getEmptyStateComponent = (text, onClick, subtitle = 'No coverages added yet') => (
    <EmptyState
      subtitle={subtitle}
      illustration={
        <RoundEmptyStateWrapper>
          <PolicyIcon />
        </RoundEmptyStateWrapper>
      }
      buttonComponent={addCoverageButton(text, onClick)}
    />
  );

  const handleGeneralExpensesSwitchChange = async (update) => {
    setIsGeneralExpensesLoading(true);
    const newConfig = {
      is_general_expenses_disabled: coverageConfiguration.is_general_expenses_disabled,
      is_general_expenses_disabled_per_sub_org: {
        ...(coverageConfiguration.is_general_expenses_disabled_per_sub_org || {}),
      },
    };
    if (update.default) {
      newConfig.is_general_expenses_disabled = update.value;
    } else if (update.subOrgId !== undefined) {
      newConfig.is_general_expenses_disabled_per_sub_org[update.subOrgId] = update.value;
    }
    try {
      await onToggleGeneralExpenses(newConfig);
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsGeneralExpensesLoading(false);
    }
  };

  // General Expenses per Sub-Org table columns and rows
  const generalExpensesColumns = [
    {
      id: 'name',
      label: organization.sub_organizations_enabled ? 'Sub-Organization' : 'Default',
      specialCell: (row) => row.name,
    },
    {
      id: 'aligned',
      label: '',
      align: 'right',
      disableSort: true,
      specialCell: (row) => (row.isAlignedToDefault ? <Label>Aligned to default</Label> : null),
    },
    {
      id: 'switch',
      label: 'General Expenses Enabled',
      align: 'right',
      disableSort: true,
      width: '50px',
      specialCell: (row) => (
        <div className="flex w-full items-center justify-end p-8">
          <Switch
            checked={row.checked}
            onChange={(_, checked) => handleGeneralExpensesSwitchChange(row.onChangeArg(checked))}
            disabled={isGeneralExpensesLoading || isLoading}
          />
        </div>
      ),
    },
  ];

  // Only sub-org rows, no default row
  const generalExpensesRows = organization.sub_organizations_enabled
    ? organization.sub_organizations.map((subOrg) => {
        const isAlignedToDefault =
          coverageConfiguration.is_general_expenses_disabled_per_sub_org?.[subOrg.id] === undefined;
        return {
          id: subOrg.id,
          name: subOrg.name,
          checked: isAlignedToDefault
            ? !coverageConfiguration.is_general_expenses_disabled
            : !coverageConfiguration.is_general_expenses_disabled_per_sub_org[subOrg.id],
          isAlignedToDefault,
          onChangeArg: (checked) => ({ subOrgId: subOrg.id, value: !checked }),
        };
      })
    : [];

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <CardDialog noCardTitle>
        <Text weight="regular" variant="small" className={styles.title}>
          {COVERAGES_SUBTITLE}
        </Text>
        <Grid container spacing={2} className="mt-20">
          {coverages?.length > 0 && (
            <>
              <Grid item xs={12} className="mb-20 mt-16">
                <Heading variant={Heading.TYPES.H3} className="pt-2">
                  Filters
                </Heading>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  className={classes.formTextField}
                  value={searchText}
                  onChange={(value) => setSearchText(value)}
                  InputProps={{
                    placeholder: 'Search Coverage',
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton title="Clear" onClick={() => setSearchText('')}>
                          <RemoveIcon className={classes.hoverableNonStrokedIcon} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={10} />
              <Grid container spacing={4}>
                {shouldShowLobFilter && (
                  <>
                    <Grid item xs={2}>
                      <MultiSelectFilter
                        label="By LOB"
                        value={selectedLOB}
                        onChange={setSelectedLOB}
                        options={supportedClaimTypes}
                        renderOption={(lob) => (
                          <>
                            <div key={lob} className="flex items-center justify-start">
                              <span className="mr-8">{getLobIcon({ lob, lobConfigurationsDict })}</span>
                              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                                {getLobDescription(lob, lobConfigurationsDict)}
                              </Text>
                            </div>
                          </>
                        )}
                        withOptionChips
                      />
                    </Grid>
                    <Grid item xs={1} />
                  </>
                )}
                {organization.sub_organizations_enabled && (
                  <Grid item xs={2}>
                    <MultiSelectFilter
                      label="By Sub-organization"
                      value={selectedSubOrgs}
                      onChange={setSelectedSubOrgs}
                      options={['all'].concat(organization.sub_organizations.map((subOrg) => subOrg.id))}
                      renderOption={(subOrgId) => (
                        <span key={subOrgId}>
                          {subOrgId === 'all' ? 'All sub-organizations' : subOrgIdToName[subOrgId]}
                        </span>
                      )}
                      withOptionChips
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
        <div className="mb-20 mt-16 flex items-center justify-between">
          {coverages?.length > 0 && (
            <Heading variant={Heading.TYPES.H3} className="pt-2">
              Coverages
            </Heading>
          )}
          <div className="ml-auto flex items-center">
            {exportToExcelButton}
            {coverages?.length > 0 && addCoverageButton('ADD COVERAGE', () => setShowCoverageDialog(true))}
          </div>
        </div>
        <SortableTable
          columns={columnsData}
          rows={coveragesFiltered}
          strikeOutRowIds={strikeOutRowIds}
          stickyHeader
          maxHeight="75vh"
          autoPaginateRowsPerPage={25}
          emptyStateComponent={getEmptyStateComponent('ADD COVERAGE', () => setShowCoverageDialog(true))}
        />
        {coverages?.length > 0 && (
          <>
            <div className="mb-20 mt-16 flex items-center justify-between">
              {sharedCoverages?.length > 0 && (
                <Heading variant={Heading.TYPES.H3} className="pt-2">
                  Shared Limits
                </Heading>
              )}
              {sharedCoverages?.length > 0 &&
                addCoverageButton('Add Shared Coverage', () => setShowSharedCoverageDialog(true))}
            </div>
            <SortableTable
              columns={sharedCoveragesColumnsData}
              rows={sharedCoverages?.filter(isSharedCoverageFitFilters)}
              stickyHeader
              maxHeight="50vh"
              autoPaginateRowsPerPage={25}
              emptyStateComponent={getEmptyStateComponent(
                'ADD SHARED COVERAGE',
                () => setShowSharedCoverageDialog(true),
                'No shared coverages added yet'
              )}
            />
          </>
        )}
        {!hideExposureConfigurationFilters && (
          <>
            {organization.sub_organizations_enabled ? (
              <>
                <div className="my-20">
                  <Heading variant={Heading.TYPES.H3} className="pt-2">
                    General Expenses Exposure
                  </Heading>
                </div>
                <div className="m-10 w-full">
                  <SortableTable
                    columns={generalExpensesColumns}
                    rows={generalExpensesRows}
                    stickyHeader
                    maxHeight="30vh"
                    className="w-full"
                  />
                </div>
                <div className="my-16">
                  <AlertBanner
                    alertType={AlertBanner.ALERT_TYPES.INFO}
                    title="New Sub-org settings"
                    note={
                      <div className="flex w-full items-center justify-between">
                        <span>Set default configuration for new sub-org added.</span>
                        <Switch
                          checked={!coverageConfiguration.is_general_expenses_disabled}
                          onChange={(_, checked) =>
                            handleGeneralExpensesSwitchChange({ default: true, value: !checked })
                          }
                          disabled={isGeneralExpensesLoading || isLoading}
                          aria-label="default general expenses switch"
                        />
                      </div>
                    }
                    withIcon
                  />
                </div>
              </>
            ) : (
              <div className="m-10 flex flex-col items-start">
                <Switch
                  checked={!coverageConfiguration.is_general_expenses_disabled}
                  onChange={(_, checked) => handleGeneralExpensesSwitchChange({ default: true, value: !checked })}
                  disabled={isGeneralExpensesLoading || isLoading}
                />
                <span className="ml-2 text-xs">General Expenses Enabled</span>
              </div>
            )}
            <div className=" mb-10 mx-10 mt-20">
              {Object.keys(COVERAGE_CONFIGURATION_BOOLEAN_FIELDS).map((fieldName) => (
                <label key={fieldName} className="flex w-fit cursor-pointer items-center pr-8">
                  <Switch
                    checked={coverageConfiguration[fieldName]}
                    onChange={(_, checked) => onUpdateCoverageConfiguration(fieldName, checked)}
                  />
                  {COVERAGE_CONFIGURATION_BOOLEAN_FIELDS[fieldName]?.desc}
                </label>
              ))}
            </div>
          </>
        )}
        {showCoverageDialog && (
          <CoverageDialog
            organization={organization}
            supportedClaimTypes={supportedClaimTypes}
            coverage={coverageToEdit}
            onClose={onClose}
            onSubmit={coverageToEdit ? onUpdateCoverage : onCreateCoverage}
            fetchUsedCoveragesKeys={fetchUsedCoveragesKeys}
            errorHandler={errorHandler}
            wizardLob={wizardLob}
            overrideDisabled={overrideDisabled}
          />
        )}
        {showSharedCoverageDialog && (
          <SharedCoverageDialog
            organization={organization}
            supportedClaimTypes={supportedClaimTypes}
            sharedCoverage={sharedCoverageToEdit}
            onClose={() => {
              setSharedCoverageToEdit(null);
              setShowSharedCoverageDialog(false);
            }}
            onSubmit={sharedCoverageToEdit ? onUpdateSharedCoverage : onCreateSharedCoverage}
            fetchUsedSharedCoveragesKeys={fetchUsedSharedCoveragesKeys}
            fetchCoverages={fetchCoverages}
            errorHandler={errorHandler}
            wizardLob={wizardLob}
            overrideDisabled={overrideDisabled}
          />
        )}
      </CardDialog>
    </LoadingSwitch>
  );
};

ExposureConfigurationTool.propTypes = {
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  organization: PropTypes.object,
  supportedClaimTypes: PropTypes.arrayOf(PropTypes.string),
  coverages: PropTypes.arrayOf(PropTypes.object),
  sharedCoverages: PropTypes.arrayOf(PropTypes.object),
  coverageConfiguration: PropTypes.object,
  onUpdateCoverage: PropTypes.func.isRequired,
  onCreateCoverage: PropTypes.func.isRequired,
  onUpdateSharedCoverage: PropTypes.func.isRequired,
  onCreateSharedCoverage: PropTypes.func.isRequired,
  onToggleGeneralExpenses: PropTypes.func.isRequired,
  onUpdateCoverageConfiguration: PropTypes.func.isRequired,
  onDeleteCoverage: PropTypes.func,
  onDeleteSharedCoverage: PropTypes.func,
  enableRemovingExistingCoverages: PropTypes.bool,
  fetchUsedCoveragesKeys: PropTypes.func.isRequired,
  fetchUsedSharedCoveragesKeys: PropTypes.func.isRequired,
  fetchCoverages: PropTypes.func.isRequired,
  errorHandler: PropTypes.func.isRequired,
  hideExposureConfigurationFilters: PropTypes.bool,
  wizardLob: PropTypes.string,
  overrideDisabled: PropTypes.bool,
  onDisable: PropTypes.func,
  onExportToExcel: PropTypes.func,
  isExporting: PropTypes.bool,
};

const TextCell = ({ value }) => <span className="capitalize">{value}</span>;

TextCell.propTypes = {
  value: PropTypes.string,
};

export { ExposureConfigurationTool };
